import {
  WORDMARK_ACCENT_FILL,
  WORDMARK_ACCENT_PATHS,
  WORDMARK_INK_PATHS,
  WORDMARK_VIEW_BOX,
} from '@sim/emcn'

const WORDMARK_FILLS = {
  body: 'var(--text-body)',
  'brand-muted': 'var(--text-tertiary)',
  inherit: 'currentColor',
  'muted-inverse': 'var(--text-muted-inverse)',
} as const

/**
 * omnyqra: heights only, the width follows the Rosby logo's aspect ratio, and
 * the nav mark sits on the baseline without Sim's optical offset (Omnyqra/sim#14,
 * Omnyqra/sim#113), see FORK.md. The heights include the logo's ascender and
 * descender, so they are larger than Sim's.
 */
const WORDMARK_SIZES = {
  nav: { height: 24, className: 'h-[24px] w-auto' },
  tag: { height: 14, className: 'h-[14px] w-auto' },
  loading: { height: 40, className: 'h-10 w-auto' },
} as const

export interface SimWordmarkProps {
  /** Navbar, compact 20px ChipTag, or centered application loading mark. */
  size?: keyof typeof WORDMARK_SIZES
  /** Body ink, inherited foreground, muted brand ink, or light ink for inverse surfaces. */
  tone?: keyof typeof WORDMARK_FILLS
}

/**
 * Canonical logotype, shared by browser and bundled desktop pages.
 *
 * omnyqra: draws the Rosby logo instead of Sim's (Omnyqra/sim#14, Omnyqra/sim#113),
 * see FORK.md. The `body` tone keeps the logo's vermilion origin dot, the
 * lettering in the tone ink; every other tone is single-color for monochrome use.
 */
export function SimWordmark({ size = 'nav', tone = 'body' }: SimWordmarkProps) {
  const { height, className } = WORDMARK_SIZES[size]
  const width = Math.round((height * WORDMARK_VIEW_BOX.width) / WORDMARK_VIEW_BOX.height)
  const fill = WORDMARK_FILLS[tone]
  const accentFill = tone === 'body' ? WORDMARK_ACCENT_FILL : fill

  return (
    <svg
      viewBox={`0 0 ${WORDMARK_VIEW_BOX.width} ${WORDMARK_VIEW_BOX.height}`}
      width={width}
      height={height}
      fill='none'
      fillRule='evenodd'
      aria-hidden='true'
      className={className}
    >
      <g fill={accentFill}>
        {WORDMARK_ACCENT_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g fill={fill}>
        {WORDMARK_INK_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  )
}
